import { forwardRef } from 'react';

import type { GeneratedAgenda } from '@/components/MeetingAgendaBuilder/agendaViewModel';

import {
    AiPromptModal,
    type AiPromptIntegrationMode,
    type AiPromptModalConfig,
    type AiPromptModalRef,
} from '@/components/AiPromptModal/AiPromptModal';

export type AgendaIntegrationMode = AiPromptIntegrationMode;
export type AgendaPromptModalRef = AiPromptModalRef;

interface AgendaPromptModalProps {
    /**
     * Runs AI agenda generation with the manager's objective + context, resolving to the parsed agenda (both
     * languages) or null on failure/timeout. The modal advances to the integration step only on a non-null result.
     */
    onGenerate: (objective: string, context: string) => Promise<GeneratedAgenda | null>;
    /** Integrates the generated agenda: 'add' appends it to the current items, 'replace' overwrites them. */
    onIntegrate: (mode: AgendaIntegrationMode, generated: GeneratedAgenda) => void;
    /** When false, "Add to agenda" is disabled — there are no existing items to append to. */
    canAdd?: boolean;
    dataTestId?: string;
}

const AGENDA_CONFIG: AiPromptModalConfig = {
    headerTitleKey: 'meetingAgenda.aiAgenda.title',
    imageAltKey: 'meetingAgenda.aiAgenda.imageAlt',
    overlayGeneratingKey: 'meetingAgenda.aiAgenda.overlay.generating',
    overlayMessageKey: 'meetingAgenda.aiAgenda.overlay.message',
    generateButtonKey: 'meetingAgenda.aiAgenda.step2.generateButton',
    steps: [
        {
            iconName: 'essential-flash',
            questionKey: 'meetingAgenda.aiAgenda.step1.question',
            placeholderKey: 'meetingAgenda.aiAgenda.step1.placeholder',
            rows: 6,
        },
        {
            iconName: 'files-folder',
            questionKey: 'meetingAgenda.aiAgenda.step2.question',
            placeholderKey: 'meetingAgenda.aiAgenda.step2.placeholder',
            rows: 5,
            optional: true,
        },
    ],
    integration: {
        titleKey: 'meetingAgenda.aiAgenda.integrate.title',
        add: {
            titleKey: 'meetingAgenda.aiAgenda.integrate.addTitle',
            descriptionKey: 'meetingAgenda.aiAgenda.integrate.addDescription',
        },
        replace: {
            titleKey: 'meetingAgenda.aiAgenda.integrate.replaceTitle',
            descriptionKey: 'meetingAgenda.aiAgenda.integrate.replaceDescription',
        },
        replaceConfirm: {
            titleKey: 'meetingAgenda.aiAgenda.replaceConfirm.title',
            messageKey: 'meetingAgenda.aiAgenda.replaceConfirm.message',
            questionKey: 'meetingAgenda.aiAgenda.replaceConfirm.question',
            confirmKey: 'meetingAgenda.aiAgenda.replaceConfirm.confirm',
            cancelKey: 'meetingAgenda.aiAgenda.replaceConfirm.cancel',
        },
    },
};

/**
 * The "Generate a Meeting Plan with Amélio AI" modal — the same multi-step AI modal as recommendations,
 * configured with the agenda's two questions (objective + context) and an Add / Replace integration step
 * (Replace shows a confirmation, since it discards the current plan).
 */
export const AgendaPromptModal = forwardRef<AgendaPromptModalRef, AgendaPromptModalProps>(
    ({ onGenerate, onIntegrate, canAdd, dataTestId = 'agenda-prompt-modal' }, ref) => (
        <AiPromptModal<GeneratedAgenda>
            ref={ref}
            config={AGENDA_CONFIG}
            onGenerate={(answers) => onGenerate(answers[0], answers[1])}
            onIntegrate={onIntegrate}
            canAdd={canAdd}
            dataTestId={dataTestId}
        />
    ),
);

AgendaPromptModal.displayName = 'AgendaPromptModal';
